import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  UserCredential,
  reload,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import {httpsCallable} from "firebase/functions";
import {auth, firestore, functions} from "./config";

export type UserRole = "player" | "dm" | "moderator" | "admin";

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  bio?: string;
  roles: UserRole[];
  isActive: boolean;
  lastActive: any;
  joinDate: any;
  characters: string[];
  isEmailVerified: boolean;
  dmTokens?: number;
  preferences?: {
    emailNotifications: {
      gameReminders: boolean;
      leagueUpdates: boolean;
    };
  };
}

export interface AuthState {
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
}

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

// Cloud Functions for role management
const setUserRole = httpsCallable(functions, "setUserRole");
const getUserClaims = httpsCallable(functions, "getUserClaims");

// Cloud Functions for custom email verification
const generateEmailVerificationToken = httpsCallable(
  functions,
  "generateEmailVerificationToken"
);
const resendEmailVerificationFunction = httpsCallable(
  functions,
  "resendEmailVerification"
);

export class AuthService {
  /**
   * Sign in with email and password
   */
  static async signInWithEmail(
    email: string,
    password: string
  ): Promise<UserCredential> {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Sign in with Google
   */
  static async signInWithGoogle(): Promise<UserCredential> {
    try {
      const result = await signInWithPopup(auth, googleProvider);

      // Check if this is a new user (first time signing in with Google)
      const userDoc = await getDoc(doc(firestore, "users", result.user.uid));

      if (!userDoc.exists()) {
        // Create user profile for new Google user
        const userProfile: Omit<UserProfile, "uid"> = {
          email: result.user.email || "",
          displayName: result.user.displayName || "Anonymous User",
          roles: ["player"], // Default to player role
          isActive: true,
          lastActive: serverTimestamp(),
          joinDate: serverTimestamp(),
          characters: [],
          isEmailVerified: result.user.emailVerified,
        };

        await setDoc(doc(firestore, "users", result.user.uid), userProfile);

        // Welcome email is enqueued by backend trigger when user doc is created and verified
      }

      return result;
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Sign up with Google (for DM signups)
   */
  static async signUpWithGoogle(
    accountType: "player" | "dm"
  ): Promise<UserCredential> {
    try {
      const result = await signInWithPopup(auth, googleProvider);

      // Check if this is a new user (first time signing in with Google)
      const userDoc = await getDoc(doc(firestore, "users", result.user.uid));

      if (!userDoc.exists()) {
        // For DM signups, always start with 'player' role and create pending approval
        const initialRoles: UserRole[] =
          accountType === "dm" ? ["player"] : [accountType];

        // Create user profile for new Google user
        const userProfile: Omit<UserProfile, "uid"> = {
          email: result.user.email || "",
          displayName: result.user.displayName || "Anonymous User",
          roles: initialRoles,
          isActive: true,
          lastActive: serverTimestamp(),
          joinDate: serverTimestamp(),
          characters: [],
          isEmailVerified: result.user.emailVerified,
        };

        await setDoc(doc(firestore, "users", result.user.uid), userProfile);

        // If this is a DM signup, create pending approval request
        if (accountType === "dm") {
          try {
            const createDMApproval = httpsCallable(
              functions,
              "createDMApproval"
            );
            await createDMApproval({
              userId: result.user.uid,
              displayName: result.user.displayName || "Anonymous User",
              email: result.user.email || "",
            });
          } catch (error) {
            // Failed to create DM approval request - non-critical error, continue
          }
        }

        // Welcome email is enqueued by backend trigger when user doc is created and verified
      }

      return result;
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Create new user account
   */
  static async createUserWithEmail(
    email: string,
    password: string,
    displayName: string,
    accountType: "player" | "dm"
  ): Promise<UserCredential> {
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Update profile with display name
      await updateProfile(result.user, {displayName});

      // For DM signups, always start with 'player' role and create pending approval
      const initialRoles: UserRole[] =
        accountType === "dm" ? ["player"] : [accountType];

      // Create user profile in Firestore
      const userProfile: Omit<UserProfile, "uid"> = {
        email,
        displayName,
        roles: initialRoles,
        isActive: true,
        lastActive: serverTimestamp(),
        joinDate: serverTimestamp(),
        characters: [],
        isEmailVerified: false,
      };

      await setDoc(doc(firestore, "users", result.user.uid), userProfile);

      // If this is a DM signup, create pending approval request
      if (accountType === "dm") {
        try {
          const createDMApproval = httpsCallable(functions, "createDMApproval");
          await createDMApproval({
            userId: result.user.uid,
            displayName,
            email,
          });
        } catch (error) {
          // Failed to create DM approval request - non-critical error, continue
        }
      }

      // Send custom email verification via MailerSend
      try {
        await generateEmailVerificationToken({userId: result.user.uid});
      } catch (verificationError: any) {
        // Log verification error but don't fail the registration
        console.warn("Failed to send verification email:", verificationError);
        // User account is still created successfully, they can resend verification later
      }
      // Welcome email is enqueued by backend trigger after verification transition

      return result;
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Sign out current user
   */
  static async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Reset password
   */
  static async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Get user profile from Firestore
   */
  static async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const userDoc = await getDoc(doc(firestore, "users", uid));
      if (userDoc.exists()) {
        return {uid, ...userDoc.data()} as UserProfile;
      }
      return null; // User document doesn't exist
    } catch (error: any) {
      // Network or other errors - throw to allow proper error handling upstream
      throw new Error(
        `Failed to fetch user profile: ${error.message || "Unknown error"}`
      );
    }
  }

  /**
   * Update user profile
   */
  static async updateUserProfile(
    uid: string,
    updates: Partial<UserProfile>
  ): Promise<void> {
    try {
      const userRef = doc(firestore, "users", uid);
      await updateDoc(userRef, {
        ...updates,
        lastActive: serverTimestamp(),
      });
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Update user settings
   */
  static async updateUserSettings(updates: {
    displayName?: string;
    bio?: string;
    preferences?: {
      emailNotifications: {
        gameReminders: boolean;
        leagueUpdates: boolean;
      };
    };
  }): Promise<void> {
    try {
      const updateSettingsFunction = httpsCallable(
        functions,
        "updateUserSettings"
      );
      await updateSettingsFunction({updates});
    } catch (error: any) {
      // Update settings error - handle specific error codes
      if (error.code === "functions/unavailable") {
        throw new Error(
          "Service temporarily unavailable. Please try again later."
        );
      } else if (error.code === "functions/invalid-argument") {
        throw new Error("Please check your input and try again.");
      } else if (error.code === "functions/permission-denied") {
        throw new Error("You do not have permission to update these settings.");
      } else if (error.code === "functions/not-found") {
        throw new Error("User account not found. Please log in again.");
      } else {
        throw new Error("Failed to update settings. Please try again.");
      }
    }
  }

  /**
   * Set user role (admin only)
   */
  static async setUserRole(uid: string, roles: UserRole[]): Promise<void> {
    try {
      await setUserRole({uid, roles});
    } catch (error: any) {
      throw new Error("Failed to update user role");
    }
  }

  /**
   * Delete user (admin only)
   */
  static async deleteUser(uid: string): Promise<void> {
    try {
      const deleteUserFunction = httpsCallable(functions, "deleteUser");
      await deleteUserFunction({uid});
    } catch (error: any) {
      // Delete user error - handle specific error codes
      if (error.code === "functions/unavailable") {
        throw new Error(
          "Cloud Functions are not available. Please check if they are deployed."
        );
      } else if (error.code === "functions/permission-denied") {
        throw new Error("Permission denied. Only admins can delete users.");
      } else if (error.code === "functions/invalid-argument") {
        throw new Error("Invalid user ID provided.");
      } else {
        throw new Error(
          `Failed to delete user: ${error.message || "Unknown error"}`
        );
      }
    }
  }

  /**
   * Delete current user's account
   */
  static async deleteAccount(): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No user logged in");
      }

      const deleteAccountFunction = httpsCallable(functions, "deleteAccount");
      await deleteAccountFunction();
    } catch (error: any) {
      // Delete account error - handle specific error codes
      if (error.code === "functions/unavailable") {
        throw new Error(
          "Service temporarily unavailable. Please try again later."
        );
      } else if (error.code === "functions/permission-denied") {
        throw new Error("You do not have permission to delete your account.");
      } else if (error.code === "functions/failed-precondition") {
        throw new Error(
          "Cannot delete account with active characters or sessions. Please resolve these first."
        );
      } else if (error.code === "functions/not-found") {
        throw new Error("User account not found. Please log in again.");
      } else {
        throw new Error("Failed to delete account. Please try again.");
      }
    }
  }

  /**
   * Get user custom claims
   */
  static async getUserClaims(): Promise<{roles: UserRole[]} | null> {
    try {
      const result = await getUserClaims();
      return result.data as {roles: UserRole[]};
    } catch (error: any) {
      // Error fetching user claims - return null for graceful handling
      return null;
    }
  }

  /**
   * Check and update email verification status
   */
  static async checkEmailVerification(): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return false;
      }

      // Check our custom verification status in Firestore first
      const userProfile = await this.getUserProfile(currentUser.uid);
      if (userProfile?.isEmailVerified) {
        return true;
      }

      // Fallback: check Firebase Auth verification status
      await reload(currentUser);
      const isVerified = currentUser.emailVerified;

      // Update Firestore if verification status changed
      if (isVerified) {
        await this.updateUserProfile(currentUser.uid, {
          isEmailVerified: true,
        });
      }

      return isVerified;
    } catch (error: any) {
      // Error checking email verification - return false for graceful handling
      return false;
    }
  }

  /**
   * Resend email verification
   */
  static async resendEmailVerification(): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No user logged in");
      }

      await resendEmailVerificationFunction();
    } catch (error: any) {
      // Handle specific error codes from the cloud function
      if (error.code === "functions/permission-denied") {
        throw new Error(
          "You do not have permission to resend verification email."
        );
      } else if (error.code === "functions/not-found") {
        throw new Error("User account not found. Please log in again.");
      } else if (error.code === "functions/already-exists") {
        throw new Error("Email is already verified.");
      } else if (error.code === "functions/resource-exhausted") {
        throw new Error(
          "Too many verification attempts. Please try again later."
        );
      } else if (error.code === "functions/unavailable") {
        throw new Error(
          "Service temporarily unavailable. Please try again later."
        );
      } else {
        throw new Error(
          "Failed to resend verification email. Please try again."
        );
      }
    }
  }

  /**
   * Get current user with latest verification status
   */
  static async getCurrentUserWithVerification(): Promise<FirebaseUser | null> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return null;
      }

      // Reload user to get latest verification status
      await reload(currentUser);
      return currentUser;
    } catch (error: any) {
      // Error getting current user - return null for graceful handling
      return null;
    }
  }

  /**
   * Listen to auth state changes
   */
  static onAuthStateChanged(
    callback: (user: UserProfile | null, error?: string) => void
  ): () => void {
    return onAuthStateChanged(
      auth,
      async (firebaseUser: FirebaseUser | null) => {
        if (firebaseUser) {
          try {
            // Get user profile from Firestore with retry logic
            let profile = await this.getUserProfile(firebaseUser.uid);

            // If profile doesn't exist but user is authenticated, it might be newly created
            // Try again after a short delay (common in production)
            if (!profile) {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              profile = await this.getUserProfile(firebaseUser.uid);
            }

            callback(profile);
          } catch (error) {
            // Profile fetch failed - pass error to callback
            callback(null, `Failed to load user profile: ${error}`);
          }
        } else {
          callback(null);
        }
      }
    );
  }

  /**
   * Get current user
   */
  static getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }

  /**
   * Check if user has specific role
   */
  static hasRole(user: UserProfile | null, role: UserRole): boolean {
    return user?.roles.includes(role) || false;
  }

  /**
   * Check if user has any of the specified roles
   */
  static hasAnyRole(user: UserProfile | null, roles: UserRole[]): boolean {
    return user?.roles.some((role) => roles.includes(role)) || false;
  }

  /**
   * Check if user is admin
   */
  static isAdmin(user: UserProfile | null): boolean {
    return this.hasRole(user, "admin");
  }

  /**
   * Check if user is DM
   */
  static isDM(user: UserProfile | null): boolean {
    return (
      this.hasRole(user, "dm") ||
      this.hasRole(user, "moderator") ||
      this.hasRole(user, "admin")
    );
  }

  /**
   * Check if user is player
   */
  static isPlayer(user: UserProfile | null): boolean {
    return (
      this.hasRole(user, "player") ||
      this.hasRole(user, "dm") ||
      this.hasRole(user, "moderator") ||
      this.hasRole(user, "admin")
    );
  }

  /**
   * Convert Firebase error codes to user-friendly messages
   */
  private static getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case "auth/user-not-found":
        return "No account found with this email address.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/invalid-email":
        return "Invalid email address.";
      case "auth/weak-password":
        return "Password should be at least 6 characters long.";
      case "auth/email-already-in-use":
        return "An account with this email already exists.";
      case "auth/too-many-requests":
        return "Too many failed attempts. Please try again later.";
      case "auth/network-request-failed":
        return "Network error. Please check your connection.";
      case "auth/popup-closed-by-user":
        return "Sign-in was cancelled.";
      case "auth/popup-blocked":
        return "Sign-in popup was blocked. Please allow popups for this site.";
      case "auth/cancelled-popup-request":
        return "Sign-in was cancelled.";
      case "auth/account-exists-with-different-credential":
        return "An account already exists with the same email address but different sign-in credentials.";
      default:
        return "An error occurred. Please try again.";
    }
  }
}

export default AuthService;
